import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Modal } from "./common/Modal";
import { Button } from "./ui/button";
import { Spinner } from "./ui/spinner";
import { subscriptionsApi, type PriceInfo } from "@/lib/api";
import {
  CreditCard,
  Shield,
  Tag,
  Check,
  AlertCircle,
  ExternalLink,
  ChevronRight,
} from "lucide-react";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  companyId: string;
  onActivated?: () => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: "SEK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function PaymentModal({
  isOpen,
  onClose,
  companyName,
  companyId,
  onActivated,
}: PaymentModalProps) {
  const [price, setPrice] = useState<PriceInfo | null>(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [campaignCode, setCampaignCode] = useState("");
  const [campaignStatus, setCampaignStatus] = useState<
    "idle" | "validating" | "valid" | "invalid"
  >("idle");
  const [campaignDiscount, setCampaignDiscount] = useState<number>(0);
  const [campaignDuration, setCampaignDuration] = useState<"forever" | "repeating" | null>(null);
  const [campaignDurationInMonths, setCampaignDurationInMonths] = useState<number | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCampaignInput, setShowCampaignInput] = useState(false);

  // Fetch price on open
  const fetchPrice = useCallback(
    async (code?: string) => {
      setIsLoadingPrice(true);
      setError(null);
      try {
        const response = await subscriptionsApi.getPrice(code);
        if (response.success && response.data) {
          setPrice(response.data);
        } else {
          setError("Kunde inte hämta pris");
        }
      } catch {
        setError("Kunde inte ansluta till servern");
        toast.error("Kunde inte hämta prisinformation");
      } finally {
        setIsLoadingPrice(false);
      }
    },
    []
  );

  useEffect(() => {
    if (isOpen) {
      fetchPrice();
      setCampaignCode("");
      setCampaignStatus("idle");
      setCampaignDiscount(0);
      setCampaignDuration(null);
      setCampaignDurationInMonths(null);
      setIsRedirecting(false);
      setError(null);
      setShowCampaignInput(false);
    }
  }, [isOpen, fetchPrice]);

  // Validate campaign code
  const handleValidateCampaign = async () => {
    if (!campaignCode.trim()) return;

    setCampaignStatus("validating");
    try {
      const response = await subscriptionsApi.validateCampaign(campaignCode.trim());
      if (response.success && response.data) {
        if (response.data.valid) {
          setCampaignStatus("valid");
          setCampaignDiscount(response.data.discountPercent || 0);
          setCampaignDuration(response.data.duration || null);
          setCampaignDurationInMonths(response.data.durationInMonths ?? null);
          // Re-fetch price with campaign code
          await fetchPrice(campaignCode.trim());
        } else {
          setCampaignStatus("invalid");
          setCampaignDiscount(0);
          setCampaignDuration(null);
          setCampaignDurationInMonths(null);
        }
      }
    } catch {
      setCampaignStatus("invalid");
    }
  };

  const handleRemoveCampaign = () => {
    setCampaignCode("");
    setCampaignStatus("idle");
    setCampaignDiscount(0);
    setCampaignDuration(null);
    setCampaignDurationInMonths(null);
    fetchPrice();
  };

  // Create checkout and redirect
  const handleCheckout = async () => {
    setIsRedirecting(true);
    setError(null);

    const appliedCampaign =
      campaignStatus === "valid" ? campaignCode.trim() : undefined;

    try {
      const response = await subscriptionsApi.createCheckout({
        companyId,
        campaignCode: appliedCampaign,
      });

      if (response.success && response.data) {
        // Check for direct activation (e.g. 100% campaign discount)
        if ("activated" in response.data && response.data.activated) {
          toast.success("Företaget har aktiverats!");
          onActivated?.();
          onClose();
          return;
        }
        // Redirect to Stripe Checkout
        if (response.data.sessionUrl) {
          window.location.href = response.data.sessionUrl;
        }
      } else {
        setError(response.error?.message || "Kunde inte starta betalning");
        setIsRedirecting(false);
      }
    } catch {
      setError("Kunde inte ansluta till betalningssystemet");
      toast.error("Kunde inte ansluta till betalningssystemet");
      setIsRedirecting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Aktivera företag" size="md">
      <div className="space-y-6">
        {/* Company info */}
        <div className="bg-gray-50 rounded-xl p-4">
          <p className="text-sm text-gray-500">Företag</p>
          <p className="font-semibold text-gray-900">{companyName}</p>
          <p className="text-sm text-gray-500 mt-1">
            {price?.subscriptionDescription || "Prenumeration"}
          </p>
        </div>

        {/* Price */}
        {isLoadingPrice ? (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        ) : price ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pris exkl. moms</span>
              <span className="text-gray-900">
                {formatCurrency(price.basePrice)}
              </span>
            </div>

            {price.discountAmount > 0 && (
              <div className="flex justify-between items-center text-green-600">
                <span className="flex items-center gap-1.5">
                  <Tag size={14} />
                  Rabatt ({price.discountPercent}%)
                </span>
                <span>-{formatCurrency(price.discountAmount)}</span>
              </div>
            )}

            {price.discountAmount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pris efter rabatt</span>
                <span className="text-gray-900">
                  {formatCurrency(price.priceExclVat)}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-gray-600">
                Moms ({Math.round(price.vatRate * 100)}%)
              </span>
              <span className="text-gray-900">
                {formatCurrency(price.vatAmount)}
              </span>
            </div>

            <div className="border-t pt-3 flex justify-between items-center">
              <span className="font-semibold text-gray-900">Totalt</span>
              <span className="text-xl font-bold text-gray-900">
                {formatCurrency(price.totalPrice)}
              </span>
            </div>
          </div>
        ) : null}

        {/* Campaign code */}
        {campaignStatus === "valid" ? (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <div className="mt-0.5 p-1 bg-green-100 rounded-full">
                  <Check size={12} className="text-green-600" />
                </div>
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-green-800 text-sm uppercase tracking-wide">
                      {campaignCode}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-200 text-green-800">
                      {campaignDiscount}% rabatt
                    </span>
                  </div>
                  {campaignDuration && (
                    <p className="text-xs text-green-600">
                      {campaignDuration === "forever"
                        ? "Gäller alla framtida förnyelser"
                        : `Gäller de första ${campaignDurationInMonths ? Math.round(campaignDurationInMonths / 12) : 1} åren`}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleRemoveCampaign}
                className="text-xs text-green-600 hover:text-green-800 hover:underline whitespace-nowrap"
              >
                Ta bort
              </button>
            </div>
          </div>
        ) : !showCampaignInput ? (
          <button
            type="button"
            onClick={() => setShowCampaignInput(true)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ChevronRight size={14} className="transition-transform" />
            Har du en kampanjkod?
          </button>
        ) : (
          <div className="space-y-2">
            <label className="text-sm text-gray-600">
              Kampanjkod
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={campaignCode}
                onChange={(e) => {
                  setCampaignCode(e.target.value);
                  if (campaignStatus === "invalid") {
                    setCampaignStatus("idle");
                  }
                }}
                placeholder="Ange kampanjkod"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleValidateCampaign();
                }}
                autoFocus
              />
              <Button
                variant="outline"
                size="default"
                onClick={handleValidateCampaign}
                disabled={
                  !campaignCode.trim() || campaignStatus === "validating"
                }
              >
                {campaignStatus === "validating" ? (
                  <Spinner size="sm" />
                ) : (
                  "Aktivera"
                )}
              </Button>
            </div>
            {campaignStatus === "invalid" && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle size={14} />
                Ogiltig eller utgången kampanjkod
              </p>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Checkout button */}
        <Button
          className="w-full h-12 text-base"
          onClick={handleCheckout}
          disabled={isRedirecting || isLoadingPrice || !price}
        >
          {isRedirecting ? (
            <>
              <Spinner size="sm" className="text-white" />
              Omdirigerar till betalning...
            </>
          ) : (
            <>
              <CreditCard size={18} />
              Prenumerera
              {price ? ` ${formatCurrency(price.totalPrice)}` : ""}
              <ExternalLink size={14} />
            </>
          )}
        </Button>

        {/* Security info */}
        <div className="flex items-start gap-2 text-xs text-gray-400">
          <Shield size={14} className="mt-0.5 shrink-0" />
          <span>
            Säker betalning via Stripe. Dina kortuppgifter hanteras av Stripe
            och lagras aldrig hos oss. Du omdirigeras till Stripes säkra
            betalningssida.
          </span>
        </div>
      </div>
    </Modal>
  );
}
